import { observer } from "mobx-react";
import { store } from "../../store";
import React from "react";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import type { UUID } from "src/utils/uuid";
import { action, computed, makeObservable } from "mobx";
import { type IColumn, SelectionMode, Selection, type IObjectWithKey } from "@fluentui/react";
import type { SelectionState } from "../../utils/selection-state";
import type {
    StationaryUnitsApiResource,
    StationaryUnitsQuery,
} from "src/domain/repositories/repository-stationary-units";
import { defaultPageSize } from "src/utils/constants";
import type { SortState } from "src/utils/sort-state";
import type { StationaryUnit, StationaryUnitSortKey } from "src/api";
import type { PaginationState } from "src/utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ListStationaryUnitItemCell } from "../atoms/list-stationary-unit-item-cell";

export interface ListStationaryUnitsProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly commandBar?: JSX.Element;
    readonly listStateId: UUID;
}

interface ListStationaryUnitItem extends IObjectWithKey {
    readonly key: UUID;
}

@observer
export class ListStationaryUnits extends React.Component<ListStationaryUnitsProps> {
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.stationaryUnits.getSelectionState(this.props.listStateId);
    }

    constructor(props: ListStationaryUnitsProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.stationaryUnits.initializeList(this.props.listStateId, {
            query: () => this.query,
        });
    }

    @computed private get sortState(): SortState<StationaryUnitSortKey> {
        return store.serviceListStates.stationaryUnits.getSortState(this.props.listStateId);
    }

    @computed
    private get paginationState(): PaginationState<StationaryUnitsApiResource> {
        return store.serviceListStates.stationaryUnits.getPaginationState(this.props.listStateId);
    }

    @computed private get query(): StationaryUnitsQuery {
        const query = {
            pageSize: defaultPageSize,
            ...this.sortState.query,
        } as StationaryUnitsQuery;
        return query;
    }

    @computed private get stationaryUnits(): (StationaryUnit | undefined)[] {
        return store.repositoryStationaryUnits.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListStationaryUnitItem | undefined)[] {
        return this.stationaryUnits.map((stationaryUnit) => {
            if (!stationaryUnit) {
                return;
            }
            return {
                key: stationaryUnit.id,
            };
        });
    }

    @computed private get columns(): IColumn[] {
        const columns: IColumn[] = [
            {
                fieldName: "serialNumber",
                name: store.i18n.t("listStationaryUnits.column.serialNumber.name"),
                key: "serialNumber",
                minWidth: 120,
                maxWidth: 120,
            },
            {
                fieldName: "label",
                name: store.i18n.t("listStationaryUnits.column.label.name"),
                key: "label",
                minWidth: 100,
            },
            {
                name: store.i18n.t("listStationaryUnits.column.createdAt.name"),
                key: "createdAt",
                minWidth: 120,
                maxWidth: 120,
            },
            {
                name: store.i18n.t("listStationaryUnits.column.firmwareLastUpdatedAt.name"),
                key: "firmwareLastUpdatedAt",
                minWidth: 120,
                maxWidth: 120,
            },
            {
                name: store.i18n.t("listStationaryUnits.column.firmwareCurrentVersion.name"),
                key: "firmwareCurrentVersion",
                minWidth: 200,
                maxWidth: 200,
            },
        ];
        return columns;
    }

    private renderItem(stationaryUnitId: UUID, column: IColumn): JSX.Element {
        return <ListStationaryUnitItemCell stationaryUnitId={stationaryUnitId} column={column} />;
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.stationaryUnits.isInitialized(this.props.listStateId)) {
            return <></>;
        }

        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...this.props}
                    selection={this.selection}
                    selectionMode={SelectionMode.multiple}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    // Disable right-click to stay consistent with other lists
                    onItemContextMenu={() => {}}
                    onRenderItemColumn={(item: ListStationaryUnitItem, _index, column) =>
                        this.renderItem(item.key, column!)
                    }
                    columns={this.sortState.patchColumns(this.columns)}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
